<template>
  <div class="font-sans flex flex-col min-h-screen bg-gradient-to-br from-blue-800 via-indigo-700 to-purple-800 relative">
    <!-- Animated Background Elements -->
    <div class="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
      <div class="absolute w-96 h-96 bg-white opacity-10 rounded-full -top-20 -left-20 animate-pulse-slow"></div>
      <div class="absolute w-80 h-80 bg-blue-300 opacity-10 rounded-full top-40 right-10 animate-float"></div>
      <div class="absolute w-64 h-64 bg-purple-300 opacity-10 rounded-full bottom-20 left-40 animate-float-delayed"></div>
    </div>
    
    <header class="py-5 px-5 flex justify-between items-center relative z-10">
      <div class="logo-text-pages flex items-center">
        <span class="text-white text-2xl">inter<span class="text-yellow-300">bot</span></span>
        <div class="ml-2 bg-yellow-300 p-1 rounded-md">
          <span class="text-sm font-bold text-blue-700">IA</span>
        </div>
      </div>
      <nav class="hidden md:flex space-x-6">
        <router-link to="/" class="text-white hover:text-yellow-300 transition-colors font-medium">Inicio</router-link>
        <router-link to="/empresas" class="text-yellow-300 font-medium">Para Empresas</router-link>
        <a href="#como-funciona" class="text-white hover:text-yellow-300 transition-colors font-medium">Cómo Funciona</a>
        <a href="#beneficios" class="text-white hover:text-yellow-300 transition-colors font-medium">Beneficios</a>
        <a href="#precios" class="text-white hover:text-yellow-300 transition-colors font-medium">Precios</a>
      </nav>
      <div class="md:hidden flex flex-col justify-between w-6 h-5 cursor-pointer">
        <span class="h-0.5 bg-white"></span>
        <span class="h-0.5 bg-white"></span>
        <span class="h-0.5 bg-white"></span>
      </div>
    </header>

    <main class="flex flex-col items-center px-5 flex-grow relative z-10">
      <!-- Hero Section -->
      <div class="text-center mb-10 max-w-4xl mt-12">
        <h1 class="text-4xl md:text-6xl font-bold leading-tight mb-6 text-white drop-shadow-lg">
          Revolucione su Proceso de Contratación con IA
        </h1>
        <p class="text-white text-xl opacity-90 max-w-2xl mx-auto mb-8">
          Automatice entrevistas iniciales, evalúe candidatos con precisión y reduzca costos de reclutamiento con nuestro sistema de entrevistas impulsado por inteligencia artificial
        </p>
        <div class="flex flex-col md:flex-row justify-center gap-4 mt-8">
          <router-link to="/create-vacancy" class="bg-gradient-to-r from-yellow-400 to-yellow-500 text-blue-900 font-bold py-4 px-8 rounded-full text-lg cursor-pointer inline-block transition-all duration-300 hover:shadow-lg hover:scale-105 transform hover:-translate-y-1 shadow-md">
            Crear Vacante
            <span class="ml-2">→</span>
          </router-link>
          <button @click="scrollToDemo" class="border-2 border-white text-white font-bold py-4 px-8 rounded-full text-lg cursor-pointer inline-block transition-all duration-300 hover:bg-white hover:bg-opacity-10 hover:shadow-lg">
            Ver Demostración
          </button>
        </div>
      </div>
      
      <!-- Stats Section -->
      <div class="w-full max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 my-16">
        <div class="bg-white bg-opacity-10 backdrop-blur-sm p-8 rounded-xl border border-white border-opacity-20 text-center transform transition-all duration-300 hover:scale-105 hover:bg-opacity-15">
          <div class="text-yellow-300 text-5xl font-bold mb-2">60%</div>
          <p class="text-white text-lg">Reducción en tiempo de contratación</p>
        </div>
        <div class="bg-white bg-opacity-10 backdrop-blur-sm p-8 rounded-xl border border-white border-opacity-20 text-center transform transition-all duration-300 hover:scale-105 hover:bg-opacity-15">
          <div class="text-yellow-300 text-5xl font-bold mb-2">80%</div>
          <p class="text-white text-lg">Más candidatos evaluados en el mismo tiempo</p>
        </div>
        <div class="bg-white bg-opacity-10 backdrop-blur-sm p-8 rounded-xl border border-white border-opacity-20 text-center transform transition-all duration-300 hover:scale-105 hover:bg-opacity-15">
          <div class="text-yellow-300 text-5xl font-bold mb-2">40%</div>
          <p class="text-white text-lg">Reducción en costos de reclutamiento</p>
        </div>
      </div>

      <!-- How it Works Section -->
      <section id="como-funciona" class="w-full max-w-6xl mx-auto py-16">
        <h2 class="text-3xl md:text-4xl font-bold text-center text-white mb-16 relative">
          <span class="bg-clip-text text-transparent bg-gradient-to-r from-yellow-200 to-yellow-400">Cómo Funciona</span>
          <div class="absolute h-1 w-20 bg-yellow-300 bottom-0 left-1/2 transform -translate-x-1/2 rounded-full"></div>
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div class="flex flex-col items-center text-center">
            <div class="w-20 h-20 bg-yellow-400 rounded-full flex items-center justify-center mb-6 shadow-lg">
              <span class="text-blue-900 text-2xl font-bold">1</span>
            </div>
            <h3 class="text-xl font-bold text-white mb-4">Configure su Vacante</h3>
            <p class="text-white text-opacity-80">Defina requisitos, habilidades clave y preguntas específicas para su posición. Personalice el perfil del candidato ideal.</p>
          </div>
          <div class="flex flex-col items-center text-center">
            <div class="w-20 h-20 bg-yellow-400 rounded-full flex items-center justify-center mb-6 shadow-lg">
              <span class="text-blue-900 text-2xl font-bold">2</span>
            </div>
            <h3 class="text-xl font-bold text-white mb-4">La IA Entrevista Candidatos</h3>
            <p class="text-white text-opacity-80">Nuestro sistema conduce entrevistas iniciales, evalúa respuestas y califica a los candidatos automáticamente.</p>
          </div>
          <div class="flex flex-col items-center text-center">
            <div class="w-20 h-20 bg-yellow-400 rounded-full flex items-center justify-center mb-6 shadow-lg">
              <span class="text-blue-900 text-2xl font-bold">3</span>
            </div>
            <h3 class="text-xl font-bold text-white mb-4">Análisis Detallado</h3>
            <p class="text-white text-opacity-80">Reciba informes completos sobre cada candidato, con evaluaciones de habilidades técnicas, soft skills y compatibilidad cultural.</p>
          </div>
        </div>
      </section>

      <!-- Demo Video Section -->
      <section id="demoVideo" class="w-full max-w-5xl mx-auto my-16 relative">
        <div class="absolute -inset-0.5 bg-gradient-to-r from-yellow-300 to-pink-500 rounded-xl blur opacity-70 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse-slow"></div>
        <div class="relative bg-white bg-opacity-10 backdrop-blur-sm rounded-xl overflow-hidden shadow-2xl p-8 border border-white border-opacity-20">
          <h2 class="text-3xl font-bold text-white mb-8 text-center">Vea cómo funciona</h2>
          
          <div class="relative w-full rounded-xl overflow-hidden shadow-2xl aspect-video">
            <div class="block">
              <img src="../assets/video-placeholder.png" alt="Video Demostración" class="w-full block transform transition duration-500 hover:scale-105"/>
            </div>
            
            <button class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white bg-opacity-20 backdrop-blur-sm rounded-full flex justify-center items-center cursor-pointer transition-all hover:scale-110 hover:bg-opacity-30 border-2 border-white border-opacity-40">
              <div class="w-0 h-0 border-t-[15px] border-b-[15px] border-l-[25px] border-transparent border-l-white ml-2"></div>
            </button>
          </div>
        </div>
      </section>

      <!-- Benefits Section -->
      <section id="beneficios" class="w-full max-w-6xl mx-auto py-16">
        <h2 class="text-3xl md:text-4xl font-bold text-center text-white mb-16 relative">
          <span class="bg-clip-text text-transparent bg-gradient-to-r from-yellow-200 to-yellow-400">Beneficios para su Empresa</span>
          <div class="absolute h-1 w-20 bg-yellow-300 bottom-0 left-1/2 transform -translate-x-1/2 rounded-full"></div>
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div class="bg-white bg-opacity-10 backdrop-blur-sm p-8 rounded-xl border border-white border-opacity-20 transform transition-all duration-300 hover:scale-105 hover:bg-opacity-15">
            <div class="w-14 h-14 bg-yellow-400 rounded-full flex items-center justify-center mb-6 shadow-lg">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-blue-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-white mb-4">Ahorro de Tiempo</h3>
            <p class="text-white text-opacity-80">Automatice las entrevistas iniciales para filtrar candidatos más rápido y permitir que su equipo de RH se enfoque en candidatos calificados.</p>
          </div>
          
          <div class="bg-white bg-opacity-10 backdrop-blur-sm p-8 rounded-xl border border-white border-opacity-20 transform transition-all duration-300 hover:scale-105 hover:bg-opacity-15">
            <div class="w-14 h-14 bg-yellow-400 rounded-full flex items-center justify-center mb-6 shadow-lg">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-blue-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-white mb-4">Evaluación Objetiva</h3>
            <p class="text-white text-opacity-80">Elimine sesgos inconscientes con análisis basados en datos y evaluaciones estandarizadas para todos los candidatos.</p>
          </div>
          
          <div class="bg-white bg-opacity-10 backdrop-blur-sm p-8 rounded-xl border border-white border-opacity-20 transform transition-all duration-300 hover:scale-105 hover:bg-opacity-15">
            <div class="w-14 h-14 bg-yellow-400 rounded-full flex items-center justify-center mb-6 shadow-lg">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-blue-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-white mb-4">Reducción de Costos</h3>
            <p class="text-white text-opacity-80">Disminuya gastos asociados con el proceso de contratación mientras aumenta la eficiencia y calidad de las contrataciones.</p>
          </div>
          
          <div class="bg-white bg-opacity-10 backdrop-blur-sm p-8 rounded-xl border border-white border-opacity-20 transform transition-all duration-300 hover:scale-105 hover:bg-opacity-15">
            <div class="w-14 h-14 bg-yellow-400 rounded-full flex items-center justify-center mb-6 shadow-lg">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-blue-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-white mb-4">Mejor Experiencia del Candidato</h3>
            <p class="text-white text-opacity-80">Ofrezca flexibilidad a los candidatos con entrevistas disponibles 24/7, retroalimentación instantánea y un proceso profesional.</p>
          </div>
        </div>
      </section>

      <!-- Pricing Section -->
      <section id="precios" class="w-full max-w-5xl mx-auto py-16">
        <h2 class="text-3xl md:text-4xl font-bold text-center text-white mb-16 relative">
          <span class="bg-clip-text text-transparent bg-gradient-to-r from-yellow-200 to-yellow-400">Planes y Precios</span>
          <div class="absolute h-1 w-20 bg-yellow-300 bottom-0 left-1/2 transform -translate-x-1/2 rounded-full"></div>
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <!-- Basic Plan -->
          <div class="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl border border-white border-opacity-20 overflow-hidden transform transition-all duration-300 hover:scale-105 hover:bg-opacity-15">
            <div class="p-8">
              <h3 class="text-xl font-bold text-white mb-2">Plan Básico</h3>
              <div class="text-yellow-300 text-4xl font-bold mb-4">$299<span class="text-lg font-normal text-white">/mes</span></div>
              <p class="text-white text-opacity-80 mb-6">Ideal para pequeñas empresas con necesidades puntuales de contratación.</p>
              <ul class="space-y-3 mb-8">
                <li class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-white">5 vacantes activas</span>
                </li>
                <li class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-white">50 entrevistas/mes</span>
                </li>
                <li class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-white">Reportes básicos</span>
                </li>
                <li class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-white">Soporte por email</span>
                </li>
              </ul>
              <button class="w-full border-2 border-yellow-400 text-yellow-400 font-bold py-3 px-8 rounded-lg text-lg cursor-pointer inline-block transition-all duration-300 hover:bg-yellow-400 hover:text-blue-900">
                Comenzar
              </button>
            </div>
          </div>
          
          <!-- Pro Plan (Highlighted) -->
          <div class="bg-gradient-to-b from-yellow-400 to-yellow-500 rounded-xl border-2 border-yellow-300 overflow-hidden transform transition-all duration-300 hover:scale-105 relative -mt-4 md:-mt-8">
            <div class="absolute top-0 left-0 w-full bg-blue-900 text-center py-1 text-yellow-300 font-bold text-sm">
              MÁS POPULAR
            </div>
            <div class="p-8 pt-12">
              <h3 class="text-xl font-bold text-blue-900 mb-2">Plan Pro</h3>
              <div class="text-blue-900 text-4xl font-bold mb-4">$799<span class="text-lg font-normal">/mes</span></div>
              <p class="text-blue-900 mb-6">Para empresas en crecimiento con contrataciones regulares.</p>
              <ul class="space-y-3 mb-8">
                <li class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-800 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-blue-900">15 vacantes activas</span>
                </li>
                <li class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-800 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-blue-900">200 entrevistas/mes</span>
                </li>
                <li class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-800 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-blue-900">Reportes avanzados</span>
                </li>
                <li class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-800 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-blue-900">Preguntas personalizadas</span>
                </li>
                <li class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-800 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-blue-900">Soporte prioritario</span>
                </li>
              </ul>
              <button class="w-full bg-blue-800 text-white font-bold py-3 px-8 rounded-lg text-lg cursor-pointer inline-block transition-all duration-300 hover:bg-blue-900">
                Comenzar
              </button>
            </div>
          </div>
          
          <!-- Enterprise Plan -->
          <div class="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl border border-white border-opacity-20 overflow-hidden transform transition-all duration-300 hover:scale-105 hover:bg-opacity-15">
            <div class="p-8">
              <h3 class="text-xl font-bold text-white mb-2">Plan Enterprise</h3>
              <div class="text-yellow-300 text-4xl font-bold mb-4">Personalizado</div>
              <p class="text-white text-opacity-80 mb-6">Para grandes empresas con necesidades específicas de reclutamiento.</p>
              <ul class="space-y-3 mb-8">
                <li class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-white">Vacantes ilimitadas</span>
                </li>
                <li class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-white">Entrevistas ilimitadas</span>
                </li>
                <li class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-white">API integración</span>
                </li>
                <li class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-white">Personalización completa</span>
                </li>
                <li class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-white">Gerente de cuenta dedicado</span>
                </li>
              </ul>
              <button class="w-full border-2 border-yellow-400 text-yellow-400 font-bold py-3 px-8 rounded-lg text-lg cursor-pointer inline-block transition-all duration-300 hover:bg-yellow-400 hover:text-blue-900">
                Contactar
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Testimonials -->
      <section class="w-full max-w-5xl mx-auto py-16">
        <h2 class="text-3xl md:text-4xl font-bold text-center text-white mb-16 relative">
          <span class="bg-clip-text text-transparent bg-gradient-to-r from-yellow-200 to-yellow-400">Lo que dicen nuestros clientes</span>
          <div class="absolute h-1 w-20 bg-yellow-300 bottom-0 left-1/2 transform -translate-x-1/2 rounded-full"></div>
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div class="bg-white bg-opacity-10 backdrop-blur-sm p-8 rounded-xl border border-white border-opacity-20 transform transition-all duration-300 hover:scale-105 hover:bg-opacity-15">
            <div class="flex items-center mb-6">
              <div class="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                <span class="text-white font-bold">TM</span>
              </div>
              <div>
                <h4 class="text-white font-bold">Tech Mexicana</h4>
                <p class="text-white text-opacity-70 text-sm">Empresa de Software</p>
              </div>
            </div>
            <p class="text-white text-opacity-80 mb-4">
              "Hemos reducido nuestro tiempo de contratación en un 40%. La plataforma nos permite evaluar más candidatos en menos tiempo y con resultados más precisos."
            </p>
            <div class="flex text-yellow-300">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
          </div>
          
          <div class="bg-white bg-opacity-10 backdrop-blur-sm p-8 rounded-xl border border-white border-opacity-20 transform transition-all duration-300 hover:scale-105 hover:bg-opacity-15">
            <div class="flex items-center mb-6">
              <div class="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mr-4">
                <span class="text-white font-bold">FL</span>
              </div>
              <div>
                <h4 class="text-white font-bold">Finanzas Latam</h4>
                <p class="text-white text-opacity-70 text-sm">Servicios Financieros</p>
              </div>
            </div>
            <p class="text-white text-opacity-80 mb-4">
              "La calidad de los candidatos que llegan a las entrevistas finales ha mejorado considerablemente. El análisis de compatibilidad cultural nos ha ahorrado mucho tiempo y recursos."
            </p>
            <div class="flex text-yellow-300">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      <!-- CTA -->
      <section class="w-full max-w-5xl mx-auto py-16 mb-8">
        <div class="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-10 text-center relative overflow-hidden">
          <div class="absolute -top-10 -left-10 w-40 h-40 bg-yellow-300 opacity-20 rounded-full"></div>
          <div class="absolute -bottom-10 -right-10 w-40 h-40 bg-yellow-300 opacity-20 rounded-full"></div>
          
          <h2 class="text-3xl font-bold text-white mb-6 relative z-10">¿Listo para revolucionar su proceso de reclutamiento?</h2>
          <p class="text-white text-opacity-90 text-lg mb-8 max-w-2xl mx-auto relative z-10">
            Únase a cientos de empresas que ya están optimizando sus procesos de contratación con nuestra plataforma de IA.
          </p>
          <div class="flex flex-col md:flex-row justify-center gap-4 relative z-10">
            <router-link to="/create-vacancy" class="bg-yellow-400 text-blue-900 font-bold py-4 px-8 rounded-full text-lg cursor-pointer inline-block transition-all duration-300 hover:bg-yellow-500 hover:shadow-lg transform hover:-translate-y-1 shadow-md">
              Crear Vacante Ahora
              <span class="ml-2">→</span>
            </router-link>
            <button @click="openContactModal" class="border-2 border-white text-white font-bold py-4 px-8 rounded-full text-lg cursor-pointer inline-block transition-all duration-300 hover:bg-white hover:text-blue-900">
              Solicitar Demostración
            </button>
          </div>
        </div>
      </section>
    </main>

    <!-- Footer -->
    <footer class="bg-gradient-to-r from-blue-900 to-indigo-900 py-10 text-white border-t border-white border-opacity-20">
      <div class="container mx-auto px-4 flex flex-col md:flex-row justify-between items-center">
        <div class="mb-6 md:mb-0 text-center md:text-left">
          <div class="logo-text-pages flex items-center justify-center md:justify-start mb-3">
            <span class="text-white text-3xl">inter<span class="text-yellow-300">bot</span></span>
            <div class="ml-2 bg-yellow-300 p-1 rounded-md">
              <span class="text-xs font-bold text-blue-700">IA</span>
            </div>
          </div>
          <p class="text-sm text-white text-opacity-80">
            © {{ new Date().getFullYear() }} Interbot. Todos los derechos reservados.
          </p>
        </div>
        <nav class="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-8 text-center md:text-left">
          <button class="text-white hover:text-yellow-300 transition-colors font-medium">
            Aviso de Privacidad
          </button>
          <button class="text-white hover:text-yellow-300 transition-colors font-medium">
            Términos de Uso
          </button>
          <button @click="openContactModal" class="bg-gradient-to-r from-yellow-400 to-yellow-500 text-blue-900 font-bold py-2 px-6 rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 transform">
            Contacto
          </button>
        </nav>
      </div>
    </footer>

    <!-- Contact Modal -->
    <div v-if="showContactModal" class="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
      <div class="bg-white p-8 rounded-lg shadow-xl max-w-md w-full relative">
        <button @click="closeContactModal" class="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-200 focus:outline-none focus:bg-gray-300">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h2 class="text-2xl font-semibold text-gray-800 mb-4">Solicitar Información</h2>
        <form @submit.prevent="submitContactForm">
          <div class="mb-4">
            <label for="companyName" class="block text-gray-700 text-sm font-bold mb-2">Nombre de la Empresa</label>
            <input type="text" id="companyName" v-model="contactForm.companyName"
              class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required>
          </div>
          <div class="mb-4">
            <label for="contactName" class="block text-gray-700 text-sm font-bold mb-2">Nombre de Contacto</label>
            <input type="text" id="contactName" v-model="contactForm.contactName"
              class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required>
          </div>
          <div class="mb-4">
            <label for="email" class="block text-gray-700 text-sm font-bold mb-2">Correo Electrónico</label>
            <input type="email" id="email" v-model="contactForm.email"
              class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required>
          </div>
          <div class="mb-4">
            <label for="phone" class="block text-gray-700 text-sm font-bold mb-2">Teléfono</label>
            <input type="tel" id="phone" v-model="contactForm.phone"
              class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required>
          </div>
          <div class="mb-4">
            <label for="message" class="block text-gray-700 text-sm font-bold mb-2">Mensaje</label>
            <textarea id="message" v-model="contactForm.message" rows="4"
              class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required></textarea>
          </div>
          <div class="flex justify-end">
            <button type="submit" class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline" :disabled="loading"
            :class="{'opacity-50 cursor-not-allowed': loading}">Enviar</button>
          </div>
          <div v-if="submissionMessage" class="text-green-500 mt-4 text-center">
            {{ submissionMessage }}
          </div>
          <div v-if="errorMessage" class="text-red-500 mt-4 text-center">
            {{ errorMessage }}
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      showContactModal: false,
      contactForm: {
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        message: '',
      },
      loading: false,
      submissionMessage: null,
      errorMessage: null,
    };
  },
  methods: {
    scrollToDemo() {
      const demoSection = document.getElementById("demoVideo");
      if (demoSection) {
        demoSection.scrollIntoView({ behavior: "smooth" });
      }
    },
    openContactModal() {
      this.showContactModal = true;
      this.loading = false;
      this.errorMessage = null;
      this.submissionMessage = null;
    },
    closeContactModal() {
      this.showContactModal = false;
      this.resetContactForm();
    },
    resetContactForm() {
      this.contactForm = {
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        message: '',
      };
    },
    async submitContactForm() {
      this.loading = true;
      
      try {
        // Here you would make an API call to your backend
        // const response = await axios.post('/api/submit-business-contact', this.contactForm);
        
        // For now simulate a successful submission
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        this.submissionMessage = 'Su solicitud ha sido enviada. Un representante se pondrá en contacto pronto.';
        this.errorMessage = null;
        this.loading = false;
        
        // Reset form after 3 seconds
        setTimeout(() => {
          this.closeContactModal();
        }, 3000);
        
      } catch (error) {
        console.error('Error submitting form:', error);
        this.errorMessage = 'Hubo un problema al enviar el formulario. Por favor intente nuevamente.';
        this.submissionMessage = null;
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
@keyframes pulse-slow {
  0%, 100% {
    opacity: 0.7;
  }
  50% {
    opacity: 0.3;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

@keyframes float-delayed {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-15px);
  }
}

.animate-pulse-slow {
  animation: pulse-slow 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.animate-float {
  animation: float 8s ease-in-out infinite;
}

.animate-float-delayed {
  animation: float-delayed 7s ease-in-out infinite;
  animation-delay: 2s;
}

/* Apply blur effect for glass morphism */
.backdrop-blur-sm {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

/* Highlight text with gradient */
.bg-clip-text {
  background-clip: text;
  -webkit-background-clip: text;
}

/* Enhanced button hover effects */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Improved card hover animations */
.hover\:scale-105:hover {
  transform: scale(1.05);
}

.hover\:-translate-y-1:hover {
  transform: translateY(-4px);
}
</style>